<script>
import Layout from "../../../layouts/main";
import appConfig from "@/app.config";
import countTo from "vue-count-to";
import axios from "axios";

export default {
  components: { Layout, countTo },
  page: {
    title: "Hasil Ujian",
    meta: [{ name: "description", content: appConfig.description }],
  },
  data() {
    return {
      title: "Hasil Ujian",
      breadcrumbs: [
        { text: "Dashboard", href: "/" },
        { text: "Student", href: "/student" },
        { text: "Hasil", active: true },
      ],
      setParamsId: this.$route.params.id,
      dataFinish: [],
      isLoading: true,
      testLulus: false,
      nilai: null,
      ringProgress: 0,
    };
  },
  computed: {
    resultMessage() {
      if (!this.testLulus) {
        return {
          title: "Belum memenuhi KKM",
          message: `Nilai kamu belum mencapai nilai minimum ${
            this.dataFinish.soal.paket === "Test Kecermatan"
              ? this.dataFinish.hasil.start_lulus
              : this.dataFinish.soal.kkm
          }.`,
        };
      }
      return {
        title: "Selamat, kamu lulus!",
        message: "Nilai kamu telah mencapai batas kelulusan.",
      };
    },
    kkmValue() {
      if (!this.dataFinish.soal) return "-";
      return this.dataFinish.soal.paket === "Test Kecermatan"
        ? this.dataFinish.hasil.start_lulus
        : this.dataFinish.soal.kkm;
    },
    paketName() {
      if (!this.dataFinish.soal) return "";
      const paket = this.dataFinish.soal.paket
        .replace(/^Test\s+/i, "")
        .trim();

      // "Test Kecermatan" is the legacy storage name. The user-facing
      // category is Sikap Kerja throughout the student application.
      return /^Kecermatan$/i.test(paket) ? "Sikap Kerja" : paket;
    },
    scoreDecimals() {
      return this.nilai !== null && this.nilai % 1 !== 0 ? 2 : 0;
    },
    ringCircumference() {
      return 2 * Math.PI * 84;
    },
    ringOffset() {
      const clamped = Math.max(0, Math.min(this.ringProgress, 100));
      return this.ringCircumference * (1 - clamped / 100);
    },
  },
  mounted() {
    this.getHasilUjian();
  },
  methods: {
    async getHasilUjian() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/json/ujian/${this.setParamsId}/finish`
        );
        this.dataFinish = response.data;

        if (this.dataFinish.soal.paket === "Test Kecermatan") {
          this.nilai = parseFloat(this.dataFinish.hasil.nilai_konversi);
          this.testLulus =
            parseInt(this.nilai) >= parseInt(this.dataFinish.hasil.start_lulus);
        } else {
          this.nilai = parseFloat(this.dataFinish.hasil.jumlah_nilai);
          this.testLulus =
            parseInt(this.nilai) >= parseInt(this.dataFinish.soal.kkm);
        }
      } catch (error) {
        console.error("Error fetching results:", error);
      } finally {
        this.isLoading = false;
        this.$nextTick(() => {
          setTimeout(() => {
            this.ringProgress = this.nilai || 0;
          }, 150);
        });
      }
    },
  },
  middleware: "authentication",
};
</script>

<template>
  <Layout>
    <div class="results-container">
      <div v-if="isLoading" class="loading-screen">
        <div class="loading-circle"></div>
        <p class="loading-text">Sedang Memuat Data...</p>
      </div>

      <div v-else class="result-content">
        <main :class="['result-card', testLulus ? 'is-passed' : 'is-failed']">
          <div class="card-accent" aria-hidden="true">
            <span class="accent-circle accent-circle-1"></span>
            <span class="accent-circle accent-circle-2"></span>
            <span class="accent-circle accent-circle-3"></span>
          </div>

          <header class="card-header">
            <p class="header-eyebrow">Hasil Ujian</p>
            <h2 class="header-paket">{{ paketName }}</h2>
          </header>

          <section class="score-hero">
            <div class="score-ring-wrap">
              <svg class="score-ring" viewBox="0 0 200 200" role="img" :aria-label="`Nilai ${nilai} dari 100`">
                <circle class="ring-track" cx="100" cy="100" r="84" />
                <circle
                  class="ring-value"
                  cx="100"
                  cy="100"
                  r="84"
                  :stroke-dasharray="ringCircumference"
                  :stroke-dashoffset="ringOffset"
                />
              </svg>
              <div class="score-ring-label">
                <countTo
                  class="score-number"
                  :startVal="0"
                  :endVal="nilai"
                  :decimals="scoreDecimals"
                  :duration="1400"
                  :options="{ useEasing: true, useGrouping: true, separator: ',', decimal: '.' }"
                />
                <span class="score-max">dari 100</span>
              </div>
            </div>

            <div class="score-copy">
              <div class="status-badge">
                <i :class="testLulus ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
                {{ testLulus ? "Lulus" : "Belum Lulus" }}
              </div>
              <h1 class="result-title">{{ resultMessage.title }}</h1>
              <p class="result-message">{{ resultMessage.message }}</p>
              <p v-if="!testLulus" class="result-guidance">
                Periksa hasil latihan dan lanjutkan latihan untuk meningkatkan nilai.
              </p>
            </div>
          </section>

          <section class="metadata-row" aria-label="Ringkasan hasil ujian">
            <div class="metadata-item">
              <div class="metadata-icon">
                <i class="fas fa-bullseye"></i>
              </div>
              <div class="metadata-text">
                <span>KKM</span>
                <strong>{{ kkmValue }}</strong>
              </div>
            </div>
            <div class="metadata-item">
              <div class="metadata-icon">
                <i class="fas fa-book-open"></i>
              </div>
              <div class="metadata-text">
                <span>Paket Soal</span>
                <strong>{{ paketName }}</strong>
              </div>
            </div>
            <div class="metadata-item">
              <div class="metadata-icon">
                <i :class="testLulus ? 'fas fa-medal' : 'fas fa-redo-alt'"></i>
              </div>
              <div class="metadata-text">
                <span>Status</span>
                <strong :class="testLulus ? 'text-passed' : 'text-failed'">
                  {{ testLulus ? "Lulus" : "Belum Lulus" }}
                </strong>
              </div>
            </div>
          </section>

          <div class="action-area">
            <button class="btn-scores" @click="$router.push('/scores')">
              <i class="fas fa-chart-line"></i>
              Lihat Nilai
            </button>
            <button class="btn-home" @click="$router.push('/')">
              <i class="fas fa-home"></i>
              Kembali ke Beranda
            </button>
          </div>
        </main>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.results-container {
  min-height: 0;
  padding: 1rem 0 3rem;
}

/* ---------- Loading ---------- */
.loading-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

.loading-circle {
  width: 48px;
  height: 48px;
  border: 3px solid #ece7f1;
  border-top: 3px solid #503668;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  margin-top: 1rem;
  font-size: 1rem;
  color: #756d7e;
}

/* ---------- Card ---------- */
.result-content {
  width: 100%;
  max-width: 880px;
  margin: 0 auto;
  padding: 0 1.25rem;
  animation: fadeUp 0.4s ease-out;
}

.result-card {
  position: relative;
  overflow: hidden;
  padding: 0 3rem 2.5rem;
  border: 1px solid #e8e3ee;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 20px 45px rgba(48, 32, 61, 0.09);
}

/* Decorative gradient band behind the header */
.card-accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 132px;
  background: linear-gradient(120deg, #503668 0%, #6d4b8c 55%, #8b5fb4 100%);
}

.result-card.is-passed .card-accent {
  background: linear-gradient(120deg, #2c6247 0%, #367a50 55%, #4d9b6c 100%);
}

.result-card.is-failed .card-accent {
  background: linear-gradient(120deg, #8f3a28 0%, #b64f37 55%, #d0704f 100%);
}

.accent-circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.09);
}

.accent-circle-1 {
  top: -70px;
  right: -40px;
  width: 200px;
  height: 200px;
}

.accent-circle-2 {
  top: 40px;
  right: 130px;
  width: 90px;
  height: 90px;
  background: rgba(255, 255, 255, 0.06);
}

.accent-circle-3 {
  top: -30px;
  left: -50px;
  width: 150px;
  height: 150px;
  background: rgba(255, 255, 255, 0.05);
}

.card-header {
  position: relative;
  padding: 2rem 0 2.6rem;
  color: #fff;
}

.header-eyebrow {
  margin: 0;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.75);
}

.header-paket {
  margin: 0.3rem 0 0;
  font-size: clamp(1.35rem, 3vw, 1.7rem);
  font-weight: 700;
  color: #fff;
}

/* ---------- Score hero ---------- */
.score-hero {
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2.75rem;
  align-items: center;
  margin-top: 0.5rem;
}

.score-ring-wrap {
  position: relative;
  width: 190px;
  height: 190px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 10px 30px rgba(48, 32, 61, 0.12);
}

.score-ring {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.ring-track {
  fill: none;
  stroke: #efeaf3;
  stroke-width: 12;
}

.ring-value {
  fill: none;
  stroke: #503668;
  stroke-width: 12;
  stroke-linecap: round;
  transition: stroke-dashoffset 1.4s cubic-bezier(0.33, 1, 0.68, 1);
}

.result-card.is-passed .ring-value {
  stroke: #367a50;
}

.result-card.is-failed .ring-value {
  stroke: #b64f37;
}

.score-ring-label {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.score-number {
  color: #30203d;
  font-size: 3rem;
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1;
}

.score-max {
  margin-top: 0.3rem;
  color: #8a8391;
  font-size: 0.8rem;
  font-weight: 600;
}

.score-copy {
  min-width: 0;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.result-card.is-passed .status-badge {
  color: #2f704a;
  background: #e3f3e8;
}

.result-card.is-failed .status-badge {
  color: #9f3f2d;
  background: #fbe9e3;
}

.result-title {
  margin: 0.9rem 0 0;
  color: #30203d;
  font-size: clamp(1.6rem, 3vw, 2rem);
  font-weight: 700;
  line-height: 1.2;
}

.result-message {
  max-width: 460px;
  margin: 0.65rem 0 0;
  color: #615967;
  font-size: 0.98rem;
  line-height: 1.6;
}

.result-guidance {
  max-width: 460px;
  margin: 0.35rem 0 0;
  color: #857c8d;
  font-size: 0.9rem;
  line-height: 1.6;
}

/* ---------- Metadata ---------- */
.metadata-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 2.5rem;
}

.metadata-item {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  min-width: 0;
  padding: 1.1rem 1.25rem;
  border: 1px solid #eee9f2;
  border-radius: 14px;
  background: #faf8fb;
}

.metadata-icon {
  display: grid;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 12px;
  background: #ece4f2;
  color: #503668;
  font-size: 1rem;
}

.result-card.is-passed .metadata-item:last-child .metadata-icon {
  background: #dcefe2;
  color: #367a50;
}

.result-card.is-failed .metadata-item:last-child .metadata-icon {
  background: #f8e5de;
  color: #b64f37;
}

.metadata-text {
  min-width: 0;
}

.metadata-text > span {
  display: block;
  color: #756d7e;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.metadata-text > strong {
  display: block;
  margin-top: 0.25rem;
  color: #30203d;
  font-size: 1.02rem;
  font-weight: 700;
  word-break: break-word;
}

.text-failed {
  color: #a5493a !important;
}

.text-passed {
  color: #377353 !important;
}

/* ---------- Actions ---------- */
.action-area {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.9rem;
  margin-top: 2.25rem;
}

.btn-scores,
.btn-home {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  min-height: 50px;
  padding: 0.75rem 1.6rem;
  border-radius: 10px;
  font-size: 0.94rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease;
}

.btn-scores {
  order: 1;
  border: 1px solid #503668;
  background: #503668;
  color: #fff;
  box-shadow: 0 8px 18px rgba(80, 54, 104, 0.28);
}

.btn-scores:hover {
  background: #432a59;
  box-shadow: 0 10px 22px rgba(80, 54, 104, 0.34);
}

.btn-home {
  order: 2;
  border: 1px solid #ded5e6;
  background: #fff;
  color: #5d466f;
}

.btn-home:hover {
  background: #f5f0f8;
}

.btn-scores:focus-visible,
.btn-home:focus-visible {
  outline: 3px solid rgba(80, 54, 104, 0.28);
  outline-offset: 2px;
}

.btn-scores:active,
.btn-home:active {
  transform: translateY(1px);
}

/* ---------- Animations ---------- */
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ---------- Responsive ---------- */
@media (max-width: 860px) {
  .metadata-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .result-content {
    padding: 0 1rem;
  }

  .result-card {
    padding: 0 1.4rem 1.75rem;
    border-radius: 16px;
  }

  .card-header {
    padding: 1.6rem 0 2.2rem;
  }

  .score-hero {
    grid-template-columns: 1fr;
    gap: 1.75rem;
    justify-items: center;
    text-align: center;
  }

  .score-ring-wrap {
    width: 165px;
    height: 165px;
  }

  .score-number {
    font-size: 2.5rem;
  }

  .result-message,
  .result-guidance {
    margin-left: auto;
    margin-right: auto;
  }

  .action-area {
    align-items: stretch;
    flex-direction: column;
    margin-top: 1.9rem;
  }

  .btn-scores,
  .btn-home {
    width: 100%;
  }
}
</style>
